// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useFetch from "@/hooks/useFetch";
import useTranslation from "@/hooks/useTranslation";
import Icon from "@/UI/atoms/Icon";
import TextInput from "@/UI/inputs/TextInput";
// --- Logic --------------------------------------------------------------------------------------
export type TySupportFormDetails = { onDone: () => void };

type Props = { onClose: () => void; details?: unknown };

/**
 * Asks us for help. Two fields, and centred rather than a side panel for the same reason the warranty
 * form is: a panel the height of the screen for two boxes is a shape that promises a form.
 *
 * **There is no edit.** A ticket is what somebody typed with the problem in front of them, and
 * letting it be rewritten afterwards would leave an admin answering a question that has moved. A
 * correction is a second ticket and the first one gets hidden.
 */
export default function SupportFormPopup({ onClose, details }: Props) {
    const t = useTranslation();
    const { onDone } = details as TySupportFormDetails;

    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");

    const { loading, error, succeededAt, invoke } = useFetch("/support", {}, false);

    useEffect(() => {
        if (!succeededAt) return;
        onClose();
        onDone();
    }, [succeededAt]); // eslint-disable-line react-hooks/exhaustive-deps

    const canSend = !!subject.trim() && !!message.trim() && !loading;

    return (
        <section className="side-form side-form--center">
            <header className="side-form__header">
                <h2 className="side-form__title">{t.support.askTitle}</h2>
                <button type="button" className="side-form__close" onClick={onClose} aria-label={t.common.close}>
                    <Icon name="close" size={16} />
                </button>
            </header>

            <div className="side-form__body">
                <p className="side-form__hint">{t.support.askHint}</p>

                {/* The lengths are the API's — it refuses a long one with its own message, and a
                    silently truncating box is worse than a sentence saying why. */}
                <TextInput value={subject} onChange={setSubject} label={t.support.fields.subject} disabled={loading} />
                <TextInput value={message} onChange={setMessage} label={t.support.fields.message} multiline rows={7} disabled={loading} />

                {error && <p className="side-form__error">{error}</p>}
            </div>

            <footer className="side-form__footer">
                <button type="button" className="side-form__cancel" onClick={onClose} disabled={loading}>
                    {t.common.cancel}
                </button>
                <button
                    type="button"
                    className="side-form__submit"
                    onClick={() => invoke({ method: "POST", data: { subject: subject.trim(), message: message.trim() } })}
                    disabled={!canSend}
                >
                    {loading ? t.common.loading : t.support.send}
                </button>
            </footer>
        </section>
    );
}
